import { DirectoryEntityType, PathType, type Path } from '@repo/api-gw-sdk';
import { Base64 } from 'js-base64';

import {
  googleWorkspaceWorkloadFromPath,
  TRASH_PATH_PREFIX_PATTERN,
} from '@/components/restore/googleWorkspaceWorkload';
import type { parseGWSAttributes } from '@/utils/gwsAttributes';
import { encodeGWSDeepLink } from '@/utils/gwsDeepLink';
import { stripDriveFileId } from '@/utils/path';

type GWSAttributes = ReturnType<typeof parseGWSAttributes>;

interface GoogleWorkspaceRestoreWorkload {
  label: string;
  wholeEntityLabel: string;
  /**
   * Number of namespace segments addressing the entity itself: users/<user>/drive,
   * users/<user>/gmail, shared_drives/<driveId>, groups/<groupId>.
   */
  entityRootDepth: number;
  /** Reads the entity's display name out of the attributes its index rows carry, when they carry one. */
  entityName?: (attributes: GWSAttributes) => string | undefined;
  /** Names the entity through the snapshot's directory index instead, for rows that carry no name. */
  directoryEntityType?: DirectoryEntityType;
  /** Replaces the File/Folder kind shown after an item's name when the namespace holds something else. */
  itemKind?: string;
}

const ownerName = (attributes: GWSAttributes) =>
  attributes.gws_owner_display_name || attributes.gws_owner_email;

type GoogleWorkspaceRestoreWorkloadName =
  NonNullable<ReturnType<typeof googleWorkspaceWorkloadFromPath>> | 'user';

const workloads: Record<
  GoogleWorkspaceRestoreWorkloadName,
  GoogleWorkspaceRestoreWorkload
> = {
  user: {
    label: 'User',
    wholeEntityLabel: 'Entire user',
    entityRootDepth: 2,
    entityName: ownerName,
  },
  drive: {
    label: 'My Drive',
    wholeEntityLabel: 'Entire drive',
    entityRootDepth: 3,
    entityName: ownerName,
  },
  sharedDrive: {
    label: 'Shared drive',
    wholeEntityLabel: 'Entire drive',
    entityRootDepth: 2,
    entityName: (attributes) => attributes.gws_shared_drive_name,
  },
  mail: {
    label: 'Gmail',
    wholeEntityLabel: 'Entire mailbox',
    entityRootDepth: 3,
    entityName: ownerName,
    itemKind: 'Email ID',
  },
  group: {
    label: 'Group',
    wholeEntityLabel: 'Entire group',
    entityRootDepth: 2,
    directoryEntityType: DirectoryEntityType.DIRECTORY_ENTITY_TYPE_GROUP,
  },
};

/** How the index names the user, shared drive, or group an item belongs to. */
export interface GoogleWorkspaceEntity {
  name?: string;
  email?: string;
}

export const googleWorkspaceEntityLabel = (
  entity?: GoogleWorkspaceEntity
): string | undefined => entity?.name || entity?.email;

export interface GoogleWorkspaceRestoreItem {
  workload: GoogleWorkspaceRestoreWorkload;
  /** Raw id of the user, shared drive, or group holding the item; index paths name entities by id only. */
  entityId: string;
  /** Listing prefix whose rows carry the entity's name attributes, trash-scoped when the item is trashed. */
  entityPrefix: string;
  isTrashed: boolean;
  item: string;
}

/**
 * A Workspace restore records the search-index key of every selected entry — a workload namespace,
 * the owning entity, and for Drive a drive-relative path carrying a ";fileId" suffix — so what the
 * job view tells about the restore is read back out of that path. A path addressing an entity root
 * rather than something below it is a whole-drive, whole-mailbox, or whole-group restore.
 */
export const parseGoogleWorkspaceRestoreItem = (
  path: string,
  pathType: PathType
): GoogleWorkspaceRestoreItem | undefined => {
  const isTrashed = TRASH_PATH_PREFIX_PATTERN.test(path);
  const segments = path
    .replace(TRASH_PATH_PREFIX_PATTERN, '')
    .split('/')
    .filter(Boolean);
  // A whole-user restore records the user itself, one level above the per-app namespaces.
  const workloadName: GoogleWorkspaceRestoreWorkloadName | undefined =
    segments[0] === 'users' && segments.length === 2
      ? 'user'
      : googleWorkspaceWorkloadFromPath(path);
  if (!workloadName) {
    return undefined;
  }

  const workload = workloads[workloadName];
  const entityRoot = segments.slice(0, workload.entityRootDepth).join('/');
  const name = segments.slice(workload.entityRootDepth).at(-1);
  const kind =
    workload.itemKind ?? (pathType === PathType.File ? 'File' : 'Folder');

  return {
    workload,
    entityId: segments[1],
    entityPrefix: `${isTrashed ? 'trash/' : ''}${entityRoot}/`,
    isTrashed,
    item: name
      ? `${stripDriveFileId(name)} (${kind})`
      : workload.wholeEntityLabel,
  };
};

export const googleWorkspaceRestoreSourceLabel = (
  item: GoogleWorkspaceRestoreItem,
  entityName?: string
): string =>
  [
    item.workload.label,
    entityName || item.entityId,
    item.isTrashed ? 'Trash' : undefined,
  ]
    .filter(Boolean)
    .join(' · ');

/**
 * The human-readable form of a Workspace index path: the ";<fileId>" suffix the index keeps on file
 * segments is dropped, and the entity id segment gives way to the entity's display name when known.
 */
export const googleWorkspaceDisplayPath = (
  path: string,
  entityName?: string
): string => {
  const segments = stripDriveFileId(path).split('/');
  if (entityName) {
    // Google ids never contain "/", so the id segment sits at a fixed offset after the namespace.
    const idIndex = TRASH_PATH_PREFIX_PATTERN.test(path) ? 2 : 1;
    if (segments.length > idIndex) {
      segments[idIndex] = entityName;
    }
  }
  return segments.join('/');
};

/**
 * Deep-link into the Workspace explorer at the restored snapshot. The explorer reads a trailing
 * slash as "open this folder" and anything else as "open the parent and highlight the item", so a
 * restored folder, drive, or mailbox opens itself while a restored file or message gets highlighted.
 * A group is opened from component state rather than the file tree, so its link addresses the group
 * itself and carries the identity (its email above all) the explorer needs in ?meta=.
 */
export const googleWorkspaceExplorerHref = (
  resourceId: string,
  file: Path,
  snapshotId?: string,
  entity?: GoogleWorkspaceEntity
): string => {
  const segments = file.path
    .replace(TRASH_PATH_PREFIX_PATTERN, '')
    .split('/')
    .filter(Boolean);
  const isGroup = segments[0] === 'groups' && segments.length > 1;
  const path = isGroup
    ? `groups/${segments[1]}/`
    : file.pathType === PathType.Directory
      ? `${file.path.replace(/\/+$/, '')}/`
      : file.path;
  const meta = isGroup
    ? encodeGWSDeepLink({ name: entity?.name, email: entity?.email })
    : undefined;

  let href = `/saas-inventory/google-workspace/${encodeURIComponent(
    resourceId
  )}?file=${encodeURIComponent(Base64.encode(path))}`;
  if (meta) {
    href += `&meta=${encodeURIComponent(meta)}`;
  }
  if (snapshotId) {
    href += `&snapshot=${encodeURIComponent(snapshotId)}`;
  }
  return href;
};
